import { AsyncPipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild,
  computed,
  effect,
  inject,
  input,
} from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { AssetDto, GalleryDto, Section, SectionObjectType, TenantDto } from '@nusign/picflow-backend-types';
import { Subject, filter, firstValueFrom, switchMap, takeUntil } from 'rxjs';
import { billingScopes } from '../../../billing-consts';
import { IsDarkColorPipe } from '../../../color-picker/is-dark-color.pipe';
import { Filter } from '../../../gallery-shared/interfaces/filter.interface';
import { loadThemeFonts } from '../../../gallery-shared/lib/load-theme-fonts';
import { GROWTHBOOK } from '../../../growthbook';
import { getGalleryBackLink } from '../../../lib/get-gallery-back-link';
import { sleep } from '../../../lib/sleep';
import { PICFLOW_ANALYTICS } from '../../../picflow-analytics';
import { PICFLOW_CHECKOUT } from '../../../picflow-checkout';
import { AppConfigService } from '../../../providers/config.service';
import { AiService } from '../../../services/ai.service';
import { AssetMoveService } from '../../../services/asset-move.service';
import { DialogService } from '../../../services/dialog.service';
import { FolderService } from '../../../services/folder.service';
import { GalleryService } from '../../../services/gallery.service';
import { NetworkService } from '../../../services/network.service';
import { PermissionService } from '../../../services/permission.service';
import { SectionService } from '../../../services/section.service';
import { SentryService } from '../../../services/sentry.service';
import { StateService } from '../../../services/state.service';
import { StorageService } from '../../../services/storage.service';
import { FlipItemDirective } from '../../../shared/directives/flip-item.directive';
import { SortByPipe } from '../../../shared/pipes/sort-by.pipe';
import { GalleryItem } from '../../classes/gallery-item.class';
import { AddSectionDialogComponent } from '../../dialogs/add-section-dialog/add-section-dialog.component';
import { calcLegacyTheme } from '../../lib/calc-legacy-theme';
import { AssetsSectionComponent } from '../assets-section/assets-section.component';
import { EditEmbedSectionComponent } from '../edit-embed-section/edit-embed-section.component';
import { EditTextSectionComponent } from '../edit-text-section/edit-text-section.component';
import { GalleryCoverComponent } from '../gallery-cover/gallery-cover.component';
import { ImageGeneratorPromptComponent } from '../image-generator-prompt/image-generator-prompt.component';
import { SubGalleriesSectionComponent } from '../sub-galleries-section/sub-galleries-section.component';
@Component({
  selector: 'app-gallery',
  templateUrl: './gallery.component.html',
  styleUrls: ['./gallery.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    GalleryCoverComponent,
    AssetsSectionComponent,
    EditTextSectionComponent,
    SubGalleriesSectionComponent,
    EditEmbedSectionComponent,
    SortByPipe,
    IsDarkColorPipe,
    AsyncPipe,
    ImageGeneratorPromptComponent,
    FlipItemDirective,
  ],
})
export class GalleryComponent implements OnInit, OnDestroy {
  public growthBook = inject(GROWTHBOOK);
  private state = inject(StateService);
  private sectionService = inject(SectionService);
  private analytics = inject(PICFLOW_ANALYTICS);
  private router = inject(Router);
  private galleryService = inject(GalleryService);
  private permissionService = inject(PermissionService);
  private assetMoveService = inject(AssetMoveService);
  private sentryService = inject(SentryService);
  private dialog = inject(DialogService);
  private folderService = inject(FolderService);
  private config = inject(AppConfigService);
  private check = inject(ChangeDetectorRef);
  private checkoutService = inject(PICFLOW_CHECKOUT);
  private networkService = inject(NetworkService);
  public aiService = inject(AiService);
  private storage = inject(StorageService);
  gallery = input<GalleryDto>(undefined);
  tenantId = computed(() => {
    const gallery = this.gallery();
    if (!gallery) return;
    if (typeof gallery.tenant === 'string') return gallery.tenant;
    return gallery.tenant.id;
  });
  isTeamMember = computed(() => {
    const tenantId = this.tenantId();
    if (!tenantId) return;
    return this.state.tenants?.some(t => t.id === tenantId);
  });
  readonly sections = input<Section[]>(undefined);
  readonly noBottomPadding = input<boolean>(undefined);
  readonly filters = input<Filter[]>([]);
  readonly clientMode = input(false);
  @ViewChild('galleryCover') cover: GalleryCoverComponent;
  @ViewChild('galleryCover', { read: ElementRef, static: true }) coverEl: ElementRef<HTMLElement>;
  scrollingPage = 0;
  startDragSection: AssetsSectionComponent;
  startDragPosition: number;
  draggingItem: GalleryItem;
  currentDragSection: AssetsSectionComponent | SubGalleriesSectionComponent;
  currentDragGallery: GalleryDto;
  draggingGalleryItem: GalleryDto;
  colorPickerOpen = false;

  public galleryPermissions = toSignal(
    toObservable(this.gallery).pipe(switchMap(gallery => this.galleryService.getGalleryPermissions(gallery)))
  );

  allowedAndEnabledFeedbackOptions = computed(() => {
    const gallery = this.gallery();
    const galleryPermissions = this.galleryPermissions();
    if (!gallery || !galleryPermissions) return;
    return this.permissionService.getAllowedAndEnabledFeedbackOptions(
      gallery,
      galleryPermissions.can_review_if_authenticated
    );
  });
  theme = computed(() => {
    const gallery = this.gallery();
    if (!gallery) return;
    return gallery.theme || calcLegacyTheme(gallery);
  });

  destroyed$ = new Subject<void>();
  recordedImgErrorCount = 0;

  constructor() {
    effect(() => {
      const sections = this.sections();
      const galleryPermissions = this.galleryPermissions();
      if (!sections || !galleryPermissions) return;
      this.addEmptySectionIfNeeded();
    });

    effect(() => {
      const theme = this.theme();
      const gallery = this.gallery();
      if (!theme || !gallery) return;
      loadThemeFonts(theme, gallery.tenant as TenantDto);
    });
  }

  ngOnInit() {
    this.galleryService.onGalleryDeleted$
      .pipe(
        takeUntil(this.destroyed$),
        filter(d => {
          if (!d) return false;
          return d.gallery.id === this.gallery()?.id;
        })
      )
      .subscribe(async () => {
        this.router.navigateByUrl(await this.getBackLink());
      });
  }

  private async addEmptySectionIfNeeded() {
    if (!this.galleryPermissions().can_edit) return;
    const sections = this.sections();
    if (sections && !sections.length) {
      const x = this.storage.get<number>('add_initial_section_' + this.gallery().id);
      if (!x || Date.now() - x > 1000) {
        this.storage.set('add_initial_section_' + this.gallery().id, Date.now());
        try {
          await this.addSection('asset', 0, true);
        } catch (e) {
          console.log(e);
        }
      }
    }
  }

  async getBackLink() {
    const isTeamMember = this.isTeamMember();
    const gallery = this.gallery();
    if (!gallery) return;
    const folderId = gallery.folder as string;
    const folderIsAlsoShared =
      !isTeamMember && folderId
        ? (await firstValueFrom(this.folderService.getFolders())).some(f => f.id === folderId)
        : false;
    return getGalleryBackLink(gallery, isTeamMember, folderIsAlsoShared);
  }

  private findSectionsToMove(position: number): Section[] {
    const sectionsToMove = [];
    const current = this.sections().find(s => s.position === position);
    if (current) {
      sectionsToMove.push(current);
      return sectionsToMove.concat(this.findSectionsToMove(position + 1));
    } else {
      return sectionsToMove;
    }
  }

  async openAddSection(position: number) {
    const resp = await this.dialog.open(AddSectionDialogComponent, { gallery: this.gallery() });
    if (resp) this.addSection(<SectionObjectType>resp, position);
  }

  async addSection(type: SectionObjectType, position: number, initial = false): Promise<Section> {
    if (type === 'gallery' && !this.permissionService.canDisplayElement(billingScopes.gallery.subGallery)) {
      this.checkoutService.open(billingScopes.gallery.subGallery);
      return;
    }

    if (type === 'embed' && !this.permissionService.canDisplayElement(billingScopes.gallery.embedSection)) {
      this.checkoutService.open(billingScopes.gallery.embedSection);
      return;
    }

    const sectionsToMove = this.findSectionsToMove(position);
    this.analytics.track('add-section', false, { type });

    const [section] = await Promise.all([
      firstValueFrom(this.sectionService.createSection(this.gallery().id, type, position, initial)),
      ...sectionsToMove.map(section => {
        section.position += 1;
        return firstValueFrom(this.sectionService.updateSection(section, { position: section.position }));
      }),
    ]);
    this.check.detectChanges();
    if (initial) return section;
    await sleep(0);

    const el = document.getElementById(`section-${section.id}`);
    if (el) {
      const sectionPosition = document.scrollingElement.scrollTop + el.getBoundingClientRect().top;
      window.scrollTo({
        top: sectionPosition - 100,
        behavior: 'smooth',
      });
    }
    return section;
  }

  onCoverChange() {
    this.cover.ngOnChanges({});
  }

  startScroll(dir: number) {
    this.scrollingPage = dir;
    this.scroll(dir);
  }

  private async scroll(dir: number) {
    if (this.scrollingPage !== dir) return;
    document.scrollingElement.scrollTop += dir;
    await sleep(20);
    this.scroll(dir);
  }

  stopScroll() {
    this.scrollingPage = 0;
  }

  async moveAsset(asset: AssetDto, gallery: GalleryDto) {
    return await this.assetMoveService.moveAssets([asset], gallery);
  }

  async toggleColorPicker() {
    if (!this.colorPickerOpen) {
      await sleep(0);
      this.colorPickerOpen = true;
      this.check.detectChanges();
    }
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }
}
